/**
 * Alerts tab of the right panel — driven by the Alert-Router feed
 * (`useAlertFeedStore`, AlertEnvelope), the curated, severity-styled, ack-able
 * subset. The fuller chronological log lives in `EventsPane`.
 *
 * Header-less by design: the panel's tab bar (mockup `rp-tabbar`) names the
 * view now, so the whole height goes to the list.
 *
 * Routing matrix lives in `./routing` — shared with EventsPane.
 */
import { Button } from '@/components/ui/button';
import type { AlertFeedItem } from '@/lib/store/alertFeed';
import { useAlertFeedStore } from '@/lib/store/alertFeed';
import { useAuthStore } from '@/lib/store/auth';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { foreignTenant } from '@/lib/tenancy';
import { cn } from '@/lib/utils';
import type { DroneId } from '@/types/ids';
import { CATEGORY_TO_TAB, routeByContext } from './routing';

function timeAgo(iso: string) {
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

type Tone = 'crit' | 'warn' | 'info';
const sevTone = (s: AlertFeedItem['severity']): Tone =>
  s === 'critical' ? 'crit' : s === 'warning' ? 'warn' : 'info';

export function AlertsPane() {
  const ctx = useUiStore((s) => s.activeContext);
  const items = useAlertFeedStore((s) => s.items);
  const ack = useAlertFeedStore((s) => s.acknowledge);
  const dismiss = useAlertFeedStore((s) => s.dismiss);
  const selectDrone = useDronesStore((s) => s.select);
  const selectedDroneId = useDronesStore((s) => s.selectedId);
  const setTab = useUiStore((s) => s.setTab);
  const setCtx = useUiStore((s) => s.setContext);
  const user = useAuthStore((s) => s.user);

  const visible = items.filter((i) => routeByContext(i, ctx, selectedDroneId));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto px-3 pt-1.5">
        {visible.length === 0 && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-6">No alerts.</div>
        )}
        {visible.map((a) => {
          const tone = sevTone(a.severity);
          const acked = a.acked_at != null;
          const navTo = () => {
            if (a.drone_id) {
              setCtx('drones');
              selectDrone(a.drone_id as DroneId);
              setTab(CATEGORY_TO_TAB[a.category] ?? 'ldt-status');
              return;
            }
            // Tenant-wide (no drone_id) alert — prefer the BE's own
            // `contexts` field when it names one of our nav contexts;
            // fall back to the category guess for jobs that don't set
            // `contexts` yet (see routing.ts).
            const ctxHint = a.contexts?.find(
              (c): c is 'oi' | 'infra' | 'surv' | 'rtm' =>
                c === 'oi' || c === 'infra' || c === 'surv' || c === 'rtm',
            );
            if (ctxHint) setCtx(ctxHint);
            else if (a.category === 'OI') setCtx('oi');
            else if (a.category === 'Infrastructure') setCtx('infra');
            else if (a.category === 'Surveillance') setCtx('surv');
            else if (a.category === 'System') setCtx('rtm');
          };
          return (
            <div
              key={a.alert_id}
              role="button"
              tabIndex={0}
              onClick={navTo}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navTo();
              }}
              className={cn(
                'grid grid-cols-[28px_10px_1fr] gap-1.5 py-2 border-b border-(--color-border-lite) items-start last:border-b-0 cursor-pointer hover:bg-(--color-bg) transition-colors',
                acked && 'opacity-60',
              )}
            >
              <span className="text-[0.692em] font-mono text-(--color-text3) text-right pt-0.5">
                {timeAgo(a.timestamp)}
              </span>
              <span
                className={cn(
                  'w-2 h-2 rounded-full mt-1 shrink-0',
                  tone === 'crit' && 'bg-(--color-red)',
                  tone === 'warn' && 'bg-(--color-amber)',
                  tone === 'info' && 'bg-(--color-blue)',
                )}
                style={{
                  boxShadow:
                    tone === 'crit'
                      ? '0 0 0 2px rgba(196,26,26,.18)'
                      : tone === 'warn'
                        ? '0 0 0 2px rgba(180,83,9,.18)'
                        : '0 0 0 2px rgba(26,95,168,.15)',
                }}
              />
              <div className="min-w-0">
                <span
                  className={cn(
                    'text-[0.846em] font-bold leading-tight font-sans',
                    tone === 'crit' && 'text-(--color-red)',
                    tone === 'warn' && 'text-(--color-amber)',
                    tone === 'info' && 'text-(--color-blue)',
                  )}
                >
                  {a.title}
                  {a.drone_id ? ` — ${a.drone_id}` : ''}
                </span>
                {/* Partner-tenant alert (payload `sourceTenant` ≠ ours).
                    Alerts from partners land on our own alert topics, so
                    without this the pane implies every alert is our fleet's. */}
                {foreignTenant(a.source_tenant) && (
                  <span className="ml-1.5 px-1 py-px rounded bg-(--color-bg) border border-(--color-border) text-(--color-text3) text-[0.615em] font-bold align-middle">
                    {foreignTenant(a.source_tenant)}
                  </span>
                )}
                <div className="text-[0.692em] text-(--color-text2) leading-snug mt-0.5">
                  {a.message}
                </div>
                <div className="flex gap-1.5 mt-2">
                  {a.requires_ack && !acked && (
                    <Button
                      size="xs"
                      variant="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        ack(a.alert_id, user?.fullName ?? 'pilot');
                      }}
                    >
                      ✓ Acknowledge
                    </Button>
                  )}
                  {!acked && (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(a.alert_id);
                      }}
                    >
                      Dismiss
                    </Button>
                  )}
                </div>
                {acked && (
                  <div className="text-[0.615em] text-(--color-text3) mt-1">
                    Acknowledged{a.acked_by ? ` by ${a.acked_by}` : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
